// @flow

import type { Action } from "redux-action";
import type { AbsencesState } from "@modules/shared/reducer/employee-absences/types";
import type { RepeatTimeLostPatternRequest } from "@modules/recording/reducer/index.js";
import { startRequest, finishRequest } from "@store/utils";
import { getRepeatAbsenceShiftPattern } from "@modules/shared/utils/repeat-absence-pattern";

export const setLoading = (state: AbsencesState) => () => startRequest(state);

export const setLoaded = (state: AbsencesState) => () => finishRequest(state);

export const onGetRevisionHistorySuccess = (state: AbsencesState) => ({ payload }: Object) => {
  return { ...state, revisionHistory: payload };
};

export const onGetRevisionHistoryError = (state: AbsencesState) => ({ payload }: Object) => {
  return { ...state, error: payload };
};

export const onGetEmployeeSMSMessagesSuccess = (state: AbsencesState) => ({ payload }: Object) => {
  return { ...state, employeeSMSMessages: payload };
};
export const onGetEmployeeSMSMessagesError = (state: AbsencesState) => ({ payload }: Object) => {
  return { ...state, error: payload };
};

export const onGetEmployeeAbsencesSuccess = (state: AbsencesState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    absenceDetails: null,
    list: payload,
  };
};

export const onGetEmployeeAbsenceSuccess = (state: AbsencesState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    absenceDetails: payload,
  };
};

export const onGetEmployeeRoleSummaryError = (state: AbsencesState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
    list: {
      ...state.list,
      content: [],
    },
  };
};

export const onGetAlertsTriggeredSuccess = (state: AbsencesState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    alerts: payload,
  };
};

export const onGetAlertsTriggeredError = (state: AbsencesState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
  };
};

export const onToggleAlertsTriggeredModal = (state: AbsencesState) => ({ payload }: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    alertsTriggered: {
      ...state.modals.alertsTriggered,
      visible: payload,
    },
  },
});

export const onToggleAddAbsenceModal = (state: AbsencesState) => ({ payload }: Object) => {
  return {
    ...state,
    modals: {
      ...state.modals,
      addAbsence: payload,
    },
  };
};

export const onSubmitTransferAbsence = (state: AbsencesState) => () => {
  return {
    ...startRequest(state),
  };
};

export const onSubmitTransferAbsenceError = (state: AbsencesState) => () => {
  return {
    ...finishRequest(state),
  };
};

export const onSubmitTransferAbsenceSuccess = (state: AbsencesState) => () => {
  return {
    ...finishRequest(state),
    modals: {
      ...state.modals,
      transferAbsenceConfirmation: {
        visible: false,
      },
      transferAbsence: {
        visible: false,
        absence: null,
      },
    },
  };
};

export const onUpdateAbsenceAddModalData = (state: AbsencesState) => ({ payload }: Object) => {
  return {
    ...state,
    modals: {
      ...state.modals,
      addAbsence: {
        ...state.modals.addAbsence,
        absence: payload,
      },
    },
  };
};

export const onGetEmployeeAbsencesSchemaSuccess = (state: AbsencesState) => ({
  payload,
}: Object) => ({
  ...finishRequest(state),
  absencesSchema: payload,
});

export const onUpdateEmployeeAbsenceSuccess = (state: AbsencesState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    absenceDetails: payload,
  };
};

export const onUpdateEmployeeAbsenceError = (state: AbsencesState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
  };
};

export const onGetEmployeeAbsenceShiftPatternSuccess = (state: AbsencesState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    absenceShiftPattern: payload,
  };
};

export const onGetEmployeeAbsenceShiftPatternOptionsSuccess = (state: AbsencesState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    absenceShiftPatternOptions: payload,
  };
};

export const onToggleEmployeeAbsenceShiftPatternModal = (state: AbsencesState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    shiftPattern: {
      ...state.modals.shiftPattern,
      visible: payload,
    },
  },
});

export const onGetEmployeeInteractionsSuccess = (state: AbsencesState) => ({
  payload,
}: Object) => ({
  ...finishRequest(state),
  interactions: payload,
});

export const onToggleEditEmployeeInteractionModal = (state: AbsencesState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    interaction: payload,
  },
});

export const onGetEmployeeAbsenceAdditionalInfoSuccess = (state: AbsencesState) => ({
  payload,
}: Object) => ({
  ...state,
  additionalInfo: payload,
});

export const onToggleEditEmployeeAbsenceAdditionalInfoModal = (state: AbsencesState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    additionalInfo: payload,
  },
});

export const onGetAbsenceStatisticsSuccess = (state: AbsencesState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    absenceStatistics: payload,
  };
};
export const onGetAbsenceStatisticsError = (state: AbsencesState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
  };
};

export const onGetEmployeeAbsenceDocumentsSuccess = (state: AbsencesState) => ({
  payload,
}: Object) => ({
  ...state,
  absenceDocuments: payload,
});

export const onToggleEmployeeAbsenceDocumentModal = (state: AbsencesState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    addAbsenceDocument: payload,
  },
});

export const onToggleDeleteEmployeeAbsenceDocumentModal = (state: AbsencesState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    deleteAbsenceDocument: payload,
  },
});

export const onGetManagerNotesSuccess = (state: AbsencesState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    managerNotes: payload,
  };
};

export const onGetManagerNotesError = (state: AbsencesState) => ({ payload }: Object) => {
  return {
    ...state,
    error: payload,
  };
};

export const onGetEmployeeAbsenceManagerNotesSuccess = (state: AbsencesState) => ({
  payload,
}: Object) => ({
  ...state,
  managerNotes: payload,
});

export const onGetEmployeeAbsenceNotificationsSuccess = (state: AbsencesState) => ({
  payload,
}: Object) => ({
  ...state,
  absenceNotifications: payload,
});

export const onToggleEmployeeAbsenceNotificationModal = (state: AbsencesState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    absenceNotification: payload,
  },
});

export const onToggleAddEditManagerNotesModal = (state: AbsencesState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    managerNotes: payload,
  },
});

export const onToggleDeleteManagerNoteModal = (state: AbsencesState) => ({ payload }: Object) => {
  const visible = !state.modals.deleteManagerNote.visible;
  return {
    ...state,
    modals: {
      ...state.modals,
      deleteManagerNote: {
        visible: !state.modals.deleteManagerNote.visible,
        note: visible ? payload.note : null,
      },
    },
  };
};

export const onGetAbsenceManagerNotesSchemaSuccess = (state: AbsencesState) => ({
  payload,
}: Object) => ({
  ...state,
  managerNotesSchema: payload,
});

export const onToggleAbsenceRateTrendRTWInterviewModal = (state: AbsencesState) => () => ({
  ...state,
  modals: {
    ...state.modals,
    rtwInterview: {
      visible: !state.modals.rtwInterview.visible,
    },
  },
});

export const onGetEmployeeNonCancelledAbsencesSuccess = (state: AbsencesState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    employeeNonCancelledAbsences: payload,
  };
};
export const onGetAbsenceNoteTypesSuccess = (state: AbsencesState) => ({ payload }: Object) => {
  return {
    ...state,
    absenceNoteTypes: payload,
  };
};

export const onRepeatTimeLostAbsenceShiftPattern = (state: AbsencesState) => ({
  payload,
}: Action<RepeatTimeLostPatternRequest>) => {
  if (!payload) {
    return state;
  }

  const newShiftPattern = getRepeatAbsenceShiftPattern(payload);
  return {
    ...state,
    absenceShiftPattern: newShiftPattern,
  };
};

export const onGetEmployeeBankHolidaysOptionsSuccess = (state: AbsencesState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    ...payload,
  };
};
